// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { useState, useCallback, useLayoutEffect, useEffect } from 'react';
const DEFAULT_OFFSET = 4;
export function useDropdownPosition({ anchorRef, dropdownRef, isActive, offset = DEFAULT_OFFSET }) {
    const [position, setPosition] = useState(null);
    const [minWidth, setMinWidth] = useState(0);
    const updatePosition = useCallback(() => {
        if (anchorRef.current === null || dropdownRef.current === null) {
            return;
        }
        const anchorRect = anchorRef.current.getBoundingClientRect();
        const dropdownRect = dropdownRef.current.getBoundingClientRect();
        setPosition(calculatePosition(anchorRect, dropdownRect, offset));
        setMinWidth(anchorRect.width);
    }, [anchorRef, dropdownRef, offset]);
    useLayoutEffect(() => {
        if (isActive) {
            updatePosition();
        }
    }, [isActive, updatePosition]);
    useEffect(() => {
        if (!isActive) {
            return;
        }
        window.addEventListener('resize', updatePosition);
        return () => {
            window.removeEventListener('resize', updatePosition);
        };
    }, [isActive, updatePosition]);
    return { position, minWidth };
}
function calculatePosition(anchorRect, dropdownRect, offset) {
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const spaceBelow = viewportHeight - anchorRect.bottom - offset;
    const spaceAbove = anchorRect.top - offset;
    const isBottom = spaceBelow >= dropdownRect.height || spaceBelow >= spaceAbove;
    const top = isBottom
        ? anchorRect.bottom + offset
        : anchorRect.top - dropdownRect.height - offset;
    let left = anchorRect.left;
    if (left + dropdownRect.width > viewportWidth) {
        left = Math.max(0, viewportWidth - dropdownRect.width - offset);
    }
    if (left < 0) {
        left = offset;
    }
    return { top, left };
}
